import React, { useEffect, useRef, useState } from "react";
import { Mic, Square, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { logger } from "../lib/logger";

const MIME_CANDIDATES = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4", "audio/ogg"];

function pickMime() {
    if (typeof MediaRecorder === "undefined") return null;
    return MIME_CANDIDATES.find(m => MediaRecorder.isTypeSupported(m)) || "";
}

export default function VoiceRecorderButton({ sessionId, onText, disabled = false, className = "" }) {
    const [state, setState] = useState("idle");
    const recRef = useRef(null);
    const chunksRef = useRef([]);
    const streamRef = useRef(null);

    const supported = typeof navigator !== "undefined" && !!navigator.mediaDevices?.getUserMedia && typeof MediaRecorder !== "undefined";

    useEffect(() => () => streamRef.current?.getTracks().forEach(t => t.stop()), []);

    const start = async () => {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            streamRef.current = stream;
            const mime = pickMime();
            const rec = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream);
            chunksRef.current = [];
            rec.ondataavailable = e => { if (e.data?.size) chunksRef.current.push(e.data); };
            rec.onstop = async () => {
                stream.getTracks().forEach(t => t.stop());
                const type = rec.mimeType || "audio/webm";
                const blob = new Blob(chunksRef.current, { type });
                if (blob.size < 1000) { setState("idle"); return; }
                setState("transcribing");
                try {
                    const ext = type.includes("mp4") ? "mp4" : type.includes("ogg") ? "webm" : "webm";
                    const res = await api.transcribe(sessionId, blob, ext);
                    if (res.text) onText(res.text);
                    else toast("Didn't catch that — try again a little closer to the mic");
                } catch (err) {
                    logger.warn("transcribe failed:", err);
                    toast.error("Couldn't transcribe. Please try again.");
                } finally {
                    setState("idle");
                }
            };
            rec.start();
            recRef.current = rec;
            setState("recording");
        } catch (err) {
            logger.warn("mic access failed:", err);
            toast.error("Microphone access is needed to speak your message.");
            setState("idle");
        }
    };

    const stop = () => {
        if (recRef.current?.state === "recording") recRef.current.stop();
    };

    if (!supported) return null;

    const recording = state === "recording";
    const busy = state === "transcribing";
    return (
        <button
            type="button"
            data-testid="voice-record-btn"
            aria-label={recording ? "Stop recording" : "Speak your message"}
            aria-pressed={recording}
            disabled={disabled || busy}
            onClick={recording ? stop : start}
            className={`shrink-0 w-11 h-11 flex items-center justify-center border transition-colors disabled:opacity-40 ${
                recording ? "bg-burgundy text-cream border-burgundy animate-pulse" : "border-burgundy/30 text-burgundy hover:bg-cream-soft"
            } ${className}`}
        >
            {busy ? <Loader2 size={18} className="animate-spin" /> : recording ? <Square size={16} /> : <Mic size={18} />}
        </button>
    );
}
